/**
 * HomeScreen Component
 * Main screen with user profile, trading panel, and action buttons
 */

import { useState, useEffect } from 'react';
import { useUserContext } from '../contexts';
import { useTranslation } from '../hooks/useTranslation';
import { useTelegram } from '../hooks/useTelegram';
import { SwapModal } from '../components/SwapModal';
import { WithdrawModal } from '../components/WithdrawModal';
import { TransferModal } from '../components/TransferModal';
import { api } from '../services/api';

interface HomeScreenProps {
  onNavigateToTrade: () => void;
  onNavigateToAssets: () => void;
  onNavigateToDeposit: () => void;
  onNavigateToTasks: () => void;
  onNavigateToStaking: () => void;
}

export function HomeScreen({ onNavigateToTrade, onNavigateToAssets, onNavigateToDeposit, onNavigateToTasks, onNavigateToStaking }: HomeScreenProps) {
  const { t } = useTranslation();
  const { user, isLoading, error: userError, refreshUser } = useUserContext();
  const { openTelegramLink } = useTelegram();
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [minedAmount, setMinedAmount] = useState(0);
  const [hasMiningPackage, setHasMiningPackage] = useState(true);
  const [hasPendingWithdrawal, setHasPendingWithdrawal] = useState(false);

  // Load mining status
  useEffect(() => {
    if (user) {
      api.getMiningStatus().then(res => {
        setMinedAmount(res.data.accumulated || 0);
      }).catch(() => {});
      api.getPortfolio().then(res => {
        const assets = res.data?.assets || [];
        setHasMiningPackage(assets.length > 0);
      }).catch(() => {});
      api.getWithdrawalHistory().then(res => {
        const withdrawals = res.data?.data || [];
        const pending = withdrawals.some((w: any) => w.status === 'pending');
        setHasPendingWithdrawal(pending);
      }).catch(() => {});
    }
  }, [user]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#fff',
      }}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  if ((userError) && !user) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ff4444',
        padding: '20px',
        textAlign: 'center',
      }}>
        <h2>{t('common.error')}</h2>
        <p>{userError}</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      color: '#fff',
      maxWidth: '480px',
      margin: '0 auto',
      paddingBottom: '80px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}>
      {/* Header: Channel/Group | Logo */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 16px 8px',
        gap: '8px',
      }}>
        <button
          onClick={() => openTelegramLink('https://t.me/WintradeVipWiton?chat')}
          style={{
            background: 'rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            color: '#60a5fa',
            fontSize: '10px',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          📢 Channel
        </button>
        <button
          onClick={() => openTelegramLink('https://t.me/WitonCommunity')}
          style={{
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            color: '#6ee7b7',
            fontSize: '10px',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          💬 Group
        </button>
        <img 
          src="/assets/logo.png" 
          alt="WiTon WINTRADE" 
          style={{
            maxWidth: '130px',
            width: '100%',
            height: 'auto',
          }}
        />
      </div>

      {/* Balance Cards */}
      <div style={{
        padding: '0 16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        marginBottom: '12px',
      }}>
        {/* WiTon Balance */}
        <div style={{
          background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)',
          borderRadius: '16px',
          padding: '12px',
          border: '1px solid rgba(123, 31, 162, 0.3)',
          boxShadow: '0 4px 12px rgba(74, 20, 140, 0.3)',
          minHeight: '140px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '8px',
          }}>
            W
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px' }}>
            WiTon Balance
          </div>
          <div style={{ fontSize: '17px', fontWeight: 'bold', wordBreak: 'break-all' }}>
            {Number(user.balance).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </div>
        </div>

        {/* USDT Balance */}
        <div style={{
          background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
          borderRadius: '16px',
          padding: '12px',
          border: '1px solid rgba(46, 125, 50, 0.3)',
          boxShadow: '0 4px 12px rgba(27, 94, 32, 0.3)',
          minHeight: '140px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '8px',
          }}>
            ₮
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px' }}>
            USDT Balance
          </div>
          <div style={{ fontSize: '17px', fontWeight: 'bold' }}>
            {(Number(user.usdtBalance) || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>
            ≈ ${(Number(user.usdtBalance) || 0).toFixed(2)}
          </div>
        </div>

        {/* Mining Income */}
        <div style={{
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          borderRadius: '16px',
          padding: '12px',
          border: '1px solid rgba(40, 53, 147, 0.3)',
          boxShadow: '0 4px 12px rgba(26, 35, 126, 0.3)',
          minHeight: '140px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #283593 0%, #3949ab 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            marginBottom: '8px',
          }}>
            ⛏️
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px' }}>
            {t('home.mining_income_day')}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#4caf50' }}>
            {minedAmount > 0 ? `${minedAmount.toLocaleString()}` : `+${Number(user.dailyIncome).toFixed(0)}`} WiTon
          </div>
        </div>
      </div>

      {/* Exchange Rate Info */}
      <div style={{
        padding: '0 16px',
        marginBottom: '12px',
      }}>
        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '10px 16px',
          border: '1px solid rgba(59, 130, 246, 0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
            1 WiTon = 0.0002 USDT
          </span>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
            Max Supply: 2.1 Billion WiTon
          </span>
        </div>
      </div>

      {/* Action Buttons Grid */}
      <div style={{
        padding: '0 16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '16px',
      }}>
        <button onClick={onNavigateToDeposit} style={{
          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>💰</span>
          <span>{t('home.deposit_usdt')}</span>
        </button>

        <button onClick={() => setIsSwapModalOpen(true)} style={{
          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
          border: '1px solid rgba(25, 118, 210, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>🔄</span>
          <span>{t('home.swap')}</span>
        </button>

        <button onClick={() => setIsTransferModalOpen(true)} style={{
          background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
          border: '1px solid rgba(156, 39, 176, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(123, 31, 162, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>📤</span>
          <span>{t('home.transfer') || 'Перевод'}</span>
        </button>

        <button onClick={() => setIsWithdrawModalOpen(true)} style={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
          border: '1px solid rgba(56, 142, 60, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>💳</span>
          <span>{t('home.withdraw')}</span>
        </button>

        <button onClick={onNavigateToAssets} style={{
          background: 'linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)',
          border: '1px solid rgba(123, 31, 162, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(106, 27, 154, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>⛏️</span>
          <span>{t('home.claim_mining')}</span>
        </button>

        <button onClick={onNavigateToTrade} style={{
          background: 'linear-gradient(135deg, #00838f 0%, #00acc1 100%)',
          border: '1px solid rgba(0, 172, 193, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(0, 131, 143, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>📈</span>
          <span>{t('home.start_trading')}</span>
        </button>

        <button onClick={onNavigateToTasks} style={{
          background: 'linear-gradient(135deg, #d84315 0%, #ff5722 100%)',
          border: '1px solid rgba(255, 87, 34, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(216, 67, 21, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>🎯</span>
          <span>{t('navigation.tasks')}</span>
        </button>

        <button onClick={onNavigateToStaking} style={{
          background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          border: '1px solid rgba(6, 182, 212, 0.3)',
          borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '14px',
          fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: '8px', boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)', transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '20px' }}>🏦</span>
          <span>{t('home.staking')}</span>
        </button>
      </div>

      <SwapModal
        isOpen={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        witonBalance={user.balance}
        usdtBalance={user.usdtBalance || 0}
        onSwapSuccess={refreshUser}
      />
      <WithdrawModal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        usdtBalance={user.usdtBalance || 0}
        onWithdrawSuccess={() => { refreshUser(); setHasPendingWithdrawal(true); }}
        hasMiningPackage={hasMiningPackage}
        hasPendingWithdrawal={hasPendingWithdrawal}
      />
      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        witonBalance={user.balance}
        onTransferSuccess={refreshUser}
      />

      {/* Company Footer */}
      <div style={{ padding: '24px 16px 16px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.8)', letterSpacing: '0.5px' }}>WINTRADE AI TECHNOLOGIES LTD</div>
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Software & Digital Technologies Company</div>
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>Established 2017 • London, United Kingdom</div>
      </div>
    </div>
  );
}
